import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useAppContext } from '@/context/AppContext';
import { useXMLOperations } from '@/hooks/useXMLOperations';
import { VehicleSortKey } from '@/types';

interface VehicleSelectorProps {
  groupType: 'vehGroups' | 'pedGroups';
  existingVehicles: string[];
  groupElement: Element;
  groupId?: string;
}

const VehicleSelectorComponent: React.FC<VehicleSelectorProps> = ({ 
  groupType, 
  existingVehicles, 
  groupElement,
  groupId = 'default'
}) => {
  const { state, dispatch } = useAppContext();
  const xmlOperations = useXMLOperations();
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [isMultiOpen, setIsMultiOpen] = useState(false);
  const [multiSelection, setMultiSelection] = useState<Set<string>>(new Set());
  const [filterText, setFilterText] = useState('');
  const [multiSortKey, setMultiSortKey] = useState<VehicleSortKey>('model');
  const [multiSortDirection, setMultiSortDirection] = useState<'asc' | 'desc'>('asc');
  const [manualInput, setManualInput] = useState('');

  // Get all vehicles for this group type (including existing ones)
  const allVehicles = useMemo(() => {
    return state.allModelsByGroup[groupType] || [];
  }, [state.allModelsByGroup, groupType]);

  const existingSet = useMemo(() => new Set(existingVehicles), [existingVehicles]);

  const sortModels = useCallback((models: string[], sortKey: VehicleSortKey, direction: 'asc' | 'desc') => {
    const sorted = [...models].sort((a, b) => {
      const vehicleA = state.vehicleData.get(a);
      const vehicleB = state.vehicleData.get(b);

      const getTextValue = (model: string, vehicle: typeof vehicleA) => {
        switch (sortKey) {
          case 'brand':
            return vehicle?.brand || '';
          case 'name':
            return vehicle?.name || '';
          case 'category':
            return vehicle?.category || '';
          case 'type':
            return vehicle?.type || '';
          default:
            return model;
        }
      };

      if (sortKey === 'price') {
        const priceA = vehicleA?.price;
        const priceB = vehicleB?.price;
        if (priceA === undefined && priceB === undefined) return 0;
        if (priceA === undefined) return 1;
        if (priceB === undefined) return -1;
        const result = priceA - priceB;
        return direction === 'asc' ? result : -result;
      }

      const textA = getTextValue(a, vehicleA).toLowerCase();
      const textB = getTextValue(b, vehicleB).toLowerCase();
      if (!textA && !textB) return 0;
      if (!textA) return 1;
      if (!textB) return -1;
      if (textA !== textB) {
        const result = textA.localeCompare(textB);
        return direction === 'asc' ? result : -result;
      }

      return a.localeCompare(b);
    });

    return sorted;
  }, [state.vehicleData]);

  const getVehicleDisplay = useCallback((model: string) => {
    const vehicle = state.vehicleData.get(model);
    if (vehicle && vehicle.source === 'lua') {
      const displayName = vehicle.brand && vehicle.name
        ? `${vehicle.brand} ${vehicle.name} (${vehicle.category})`
        : model;
      return {
        displayName,
        details: `Brand: ${vehicle.brand}\nCategory: ${vehicle.category}\nPrice: ${vehicle.price.toLocaleString()}`
      };
    }

    return {
      displayName: model,
      details: `Model: ${model}`
    };
  }, [state.vehicleData]);

  const handleVehicleChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedModel = event.target.value;
    setSelectedVehicle(selectedModel);
    
    if (selectedModel) {
      // Check if vehicle is already in the group using Set for better performance
      if (existingSet.has(selectedModel)) {
        dispatch({ type: 'ADD_ERROR', payload: `Vehicle "${selectedModel}" is already in this group.` });
        setSelectedVehicle('');
        return;
      }
      
      // Add vehicle to group
      xmlOperations.addVehicleToGroup(groupElement, selectedModel);
      
      // Reset selection
      setSelectedVehicle('');
    }
  }, [dispatch, xmlOperations, groupElement, existingSet]);

  const handleManualAdd = useCallback(() => {
    const model = manualInput.trim();
    if (!model) {
      return;
    }

    // Check if vehicle is already in the group
    if (existingSet.has(model)) {
      dispatch({ type: 'ADD_ERROR', payload: `Vehicle "${model}" is already in this group.` });
      return;
    }

    // Add manual vehicle to state
    dispatch({ type: 'ADD_MANUAL_VEHICLE', payload: { model, groupType } });

    // Add vehicle to group
    xmlOperations.addVehicleToGroup(groupElement, model);

    // Clear input
    setManualInput('');
  }, [manualInput, existingSet, groupType, dispatch, xmlOperations, groupElement]);

  // Memoize vehicle options to prevent recreation on every render
  const vehicleOptions = useMemo(() => {
    const sortedModels = sortModels(allVehicles, 'model', 'asc');

    return sortedModels.map(model => {
      const { displayName, details } = getVehicleDisplay(model);
      const isExisting = existingSet.has(model);
      const vehicle = state.vehicleData.get(model);
      const optionClass = vehicle?.source === 'lua' ? 'lua-vehicle-option' : 'xml-vehicle-option';
      const warningSuffix = isExisting ? '\n⚠️ Already in group' : '';

      return (
        <option
          key={model}
          value={model}
          title={`${details}${warningSuffix}`}
          className={`${optionClass} ${isExisting ? 'existing-vehicle-option' : ''}`}
        >
          {displayName}{isExisting ? ' ⚠️' : ''}
        </option>
      );
    });
  }, [allVehicles, state.vehicleData, existingSet, getVehicleDisplay, sortModels]);

  const filteredVehicles = useMemo(() => {
    const normalizedFilter = filterText.trim().toLowerCase();
    if (!normalizedFilter) {
      return allVehicles;
    }

    return allVehicles.filter(model => {
      const vehicle = state.vehicleData.get(model);
      const searchText = [
        model,
        vehicle?.brand,
        vehicle?.name,
        vehicle?.category
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchText.includes(normalizedFilter);
    });
  }, [allVehicles, filterText, state.vehicleData]);

  const sortedFilteredVehicles = useMemo(() => {
    return sortModels(filteredVehicles, multiSortKey, multiSortDirection);
  }, [filteredVehicles, multiSortKey, multiSortDirection, sortModels]);

  const selectableFiltered = useMemo(() => {
    return sortedFilteredVehicles.filter(model => !existingSet.has(model));
  }, [sortedFilteredVehicles, existingSet]);

  const allFilteredSelected = useMemo(() => {
    return selectableFiltered.length > 0 && selectableFiltered.every(model => multiSelection.has(model));
  }, [selectableFiltered, multiSelection]);

  const selectedCount = useMemo(() => multiSelection.size, [multiSelection]);

  const handleOpenMulti = useCallback(() => {
    setIsMultiOpen(true);
    setFilterText('');
    setMultiSelection(new Set());
  }, []);

  const handleCloseMulti = useCallback(() => {
    setIsMultiOpen(false);
    setFilterText('');
    setMultiSelection(new Set());
  }, []);

  const handleToggleMultiSelection = useCallback((model: string, selected: boolean) => {
    setMultiSelection(prev => {
      const next = new Set(prev);
      if (selected) {
        next.add(model);
      } else {
        next.delete(model);
      }
      return next;
    });
  }, []);

  const handleToggleFiltered = useCallback(() => {
    setMultiSelection(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        selectableFiltered.forEach(model => next.delete(model));
      } else {
        selectableFiltered.forEach(model => next.add(model));
      }
      return next;
    });
  }, [allFilteredSelected, selectableFiltered]);

  const handleAddMultiple = useCallback(() => {
    if (multiSelection.size === 0) return;

    const modelsToAdd = Array.from(multiSelection).filter(model => !existingSet.has(model));
    if (modelsToAdd.length > 0) {
      xmlOperations.addVehiclesToGroup(groupElement, modelsToAdd);
    }

    handleCloseMulti();
  }, [multiSelection, existingSet, xmlOperations, groupElement, handleCloseMulti]);

  useEffect(() => {
    if (!isMultiOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseMulti();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMultiOpen, handleCloseMulti]);

  const handleSortDirectionToggle = useCallback(() => {
    setMultiSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
  }, []);

  if (allVehicles.length === 0) {
    return (
      <div className="add-vehicle-section">
        <span className="add-vehicle-label">Add Vehicle:</span>
        <span className="no-vehicles-available">
          No vehicles available
        </span>
      </div>
    );
  }

  return (
    <div className="add-vehicle-section">
      <label htmlFor={`vehicle-selector-${groupId}`} className="add-vehicle-label">
        Add Vehicle:
      </label>
      <select
        id={`vehicle-selector-${groupId}`}
        value={selectedVehicle}
        onChange={handleVehicleChange}
        className="add-vehicle-dropdown"
      >
        <option value="">Select a vehicle...</option>
        {vehicleOptions}
      </select>
      <button
        type="button"
        className="add-multiple-btn"
        onClick={handleOpenMulti}
      >
        Add Multiple
      </button>
      <div className="manual-add-section">
        <input
          type="text"
          value={manualInput}
          onChange={(e) => setManualInput(e.target.value)}
          placeholder="Enter model name manually..."
          className="manual-vehicle-input"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleManualAdd();
            }
          }}
        />
        <button
          type="button"
          className="add-manual-btn"
          onClick={handleManualAdd}
          disabled={!manualInput.trim()}
        >
          Add
        </button>
      </div>
      {isMultiOpen && (
        <div className="modal-backdrop" onClick={handleCloseMulti}>
          <div
            className="modal-content add-multiple-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`add-multiple-title-${groupId}`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <h4 id={`add-multiple-title-${groupId}`}>Add Multiple Vehicles</h4>
              <button
                type="button"
                className="modal-close-btn"
                onClick={handleCloseMulti}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-toolbar">
                <input
                  type="text"
                  value={filterText}
                  onChange={(event) => setFilterText(event.target.value)}
                  placeholder="Search vehicles"
                  className="vehicle-search-input"
                />
                <select
                  className="modal-sort-select"
                  value={multiSortKey}
                  onChange={(event) => setMultiSortKey(event.target.value as VehicleSortKey)}
                >
                  <option value="model">Model</option>
                  <option value="brand">Brand</option>
                  <option value="name">Name</option>
                  <option value="category">Category</option>
                  <option value="price">Price</option>
                  <option value="type">Type</option>
                </select>
                <button
                  type="button"
                  className="sort-direction-btn"
                  onClick={handleSortDirectionToggle}
                >
                  {multiSortDirection === 'asc' ? 'Asc' : 'Desc'}
                </button>
                <button
                  type="button"
                  className="select-filtered-btn"
                  onClick={handleToggleFiltered}
                  disabled={selectableFiltered.length === 0}
                >
                  {allFilteredSelected ? 'Clear Filtered' : 'Select Filtered'}
                </button>
              </div>
              <div className="vehicle-selection-list">
                {sortedFilteredVehicles.length === 0 ? (
                  <div className="no-vehicles-available">No vehicles match this search.</div>
                ) : (
                  sortedFilteredVehicles.map(model => {
                    const { displayName, details } = getVehicleDisplay(model);
                    const isExisting = existingSet.has(model);
                    const isSelected = multiSelection.has(model);
                    const vehicle = state.vehicleData.get(model);
                    const rowClass = vehicle?.source === 'lua' ? 'lua-vehicle-row' : 'xml-vehicle-row';

                    return (
                      <label
                        key={model}
                        className={`vehicle-select-row ${rowClass} ${isExisting ? 'vehicle-select-row-disabled' : ''}`}
                        title={`${details}${isExisting ? '\nAlready in group' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isExisting}
                          onChange={(event) => handleToggleMultiSelection(model, event.target.checked)}
                        />
                        <span className="vehicle-select-name">{displayName}</span>
                        {isExisting && <span className="vehicle-select-status">Already in group</span>}
                      </label>
                    );
                  })
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="modal-cancel-btn" onClick={handleCloseMulti}>
                Cancel
              </button>
              <button
                type="button"
                className="modal-confirm-btn"
                onClick={handleAddMultiple}
                disabled={selectedCount === 0}
              >
                Add Selected ({selectedCount})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const VehicleSelector = React.memo(VehicleSelectorComponent);
VehicleSelector.displayName = 'VehicleSelector';
